import { useState } from "react";
import { Download, ChevronDown } from "lucide-react";
import { Button } from "@qrody/ui";
import { useTranslations } from "@/i18n/IntlProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FORMATS = ["png", "svg", "pdf"] as const;
type Format = (typeof FORMATS)[number];

/**
 * Format-picker + single Download button — replaces the legacy panel's
 * three per-format buttons with one dropdown-menu (shadcn) and one
 * primary button, mirroring the polish from commits eab1d60 / 806fb01 /
 * df97a37.
 *
 * Picking a format inside the dropdown fires `window.location.assign`
 * straight at the same-origin BFF image endpoint — the HttpOnly session
 * cookie travels automatically. Triggering a navigation instead of a
 * fetch+blob keeps the URL visible in the address bar long enough for
 * the browser's native "Save as…" dialog to label the file, and avoids
 * the extra round trip through `URL.createObjectURL`.
 */
export function CodeDownloadDropdown({ id }: { id: string }) {
  const t = useTranslations("app.codes.detail.downloads");
  const [format, setFormat] = useState<Format>("png");

  const downloadAt = (next: Format) => {
    setFormat(next);
    // Same-origin GET — the SessionBearerInjectionFilter injects the
    // bearer for /v1/* calls, the browser handles the actual file save.
    window.location.assign(
      `/v1/codes/${encodeURIComponent(id)}/image?format=${next}`,
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            {t(`format.${format}`)}
            <ChevronDown className="size-3.5" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t("formatLabel")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {FORMATS.map((f) => (
            <DropdownMenuItem
              key={f}
              onSelect={() => downloadAt(f)}
              data-active={f === format ? true : undefined}
            >
              {t(`format.${f}`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        size="sm"
        onClick={() => downloadAt(format)}
        className="gap-1.5"
      >
        <Download className="size-4" aria-hidden />
        {t("download")}
      </Button>
    </div>
  );
}
